/**
 * Apps — the launcher's view of the navigation.
 *
 * The flat sidebar listed every screen in the product (≈430 of them) under one
 * long run of part headings, which made it accurate and unusable: finding
 * "Bank statements" meant scrolling past Enterprise, all of Finance and half of
 * Treasury. An app is one FSD part (plus the two pinned containers, Home and
 * Me) presented the way a phone presents one: a tinted tile on the home screen
 * and in the top-bar launcher, and — once you are inside it — a sidebar that
 * lists only that part's screens.
 *
 * Apps cover FSD Parts 1–15 one-to-one. FSD Part 16 is Cross-Module
 * Integration — requirements only, no screens — so it has no app. The
 * Platform app holds the Part D cross-cutting specifications and Part 17 (AI),
 * and is captioned `'D · 17'`.
 *
 * This file adds NO new navigation data. Each app points at an existing
 * `NavPart` by id, so the item list, permissions, labels and the FSD
 * extraneous markers all keep coming from `nav/parts/*` exactly as before;
 * an app only adds what a launcher needs on top — a glyph, a tint and a
 * one-line tagline. `NAV_APPS` and `NAV_PARTS` are cross-checked by
 * `apps.test.ts` so a part can never exist without an app or vice versa.
 */

import type { AppIconName } from '@/lib/icons'

import { NAV_ROUTE_ALIASES } from './aliases'
import {
  NAV_PARTS,
  visibleParts,
  type NavGroup,
  type NavItem,
  type NavPart,
  type PermissionCheck,
} from './items'

/**
 * The two stops of an app tile's vertical gradient, light (top) to deep
 * (bottom) — the way an iOS icon is lit from above. The glyph is always white,
 * so every `to` is dark enough to hold white at ≥ 3:1 (WCAG 1.4.11, non-text
 * contrast); `from` only has to read as the same hue, lighter.
 */
export interface AppTint {
  from: string
  to: string
}

export interface NavApp {
  /** Stable id. Also the localStorage key for per-app sidebar preferences. */
  id: string
  /** The `NavPart.id` this app presents. */
  partId: string
  /** Visible name, without the FSD part number ("Finance", not "2 Finance"). */
  labelKey: string
  /** One-line tagline, shown under the name on the home screen. */
  descriptionKey: string
  glyph: AppIconName
  tint: AppTint
  /**
   * The FSD part caption: `'1'`…`'15'` for the part apps, `'D · 17'` for
   * Platform (Part D cross-cutting specs + Part 17 AI), `null` for the pinned
   * containers (Home, Me). No app carries `'16'`: Part 16 (Cross-Module
   * Integration) is requirements only, with no screens. A string, not a
   * number, because Platform's caption is not a single part number.
   */
  fsdPart: string | null
  /**
   * Whether the app has a tile in the launcher and on the home grid. Home does
   * not: it IS the home screen, reached from the brand mark and the launcher's
   * own "Home" row. It still exists as an app so `/approvals` and friends get
   * a sidebar like every other screen does.
   */
  inLauncher: boolean
}

export const HOME_APP_ID = 'home'

export const NAV_APPS: readonly NavApp[] = [
  {
    id: 'home',
    partId: 'home',
    labelKey: 'nav.apps.home.label',
    descriptionKey: 'nav.apps.home.description',
    glyph: 'home',
    tint: { from: '#2dd4bf', to: '#0f766e' },
    fsdPart: null,
    inLauncher: false,
  },
  {
    id: 'enterprise',
    partId: 'p01-enterprise',
    labelKey: 'nav.apps.enterprise.label',
    descriptionKey: 'nav.apps.enterprise.description',
    glyph: 'building',
    tint: { from: '#818cf8', to: '#4338ca' },
    fsdPart: '1',
    inLauncher: true,
  },
  {
    id: 'finance',
    partId: 'p02-finance',
    labelKey: 'nav.apps.finance.label',
    descriptionKey: 'nav.apps.finance.description',
    glyph: 'landmark',
    tint: { from: '#34d399', to: '#047857' },
    fsdPart: '2',
    inLauncher: true,
  },
  {
    id: 'treasury',
    partId: 'p03-treasury',
    labelKey: 'nav.apps.treasury.label',
    descriptionKey: 'nav.apps.treasury.description',
    glyph: 'wallet',
    tint: { from: '#2dd4bf', to: '#0f766e' },
    fsdPart: '3',
    inLauncher: true,
  },
  {
    id: 'procurement',
    partId: 'p04-procurement',
    labelKey: 'nav.apps.procurement.label',
    descriptionKey: 'nav.apps.procurement.description',
    glyph: 'cart',
    tint: { from: '#fdba74', to: '#c2410c' },
    fsdPart: '4',
    inLauncher: true,
  },
  {
    id: 'sales',
    partId: 'p05-sales',
    labelKey: 'nav.apps.sales.label',
    descriptionKey: 'nav.apps.sales.description',
    glyph: 'tag',
    tint: { from: '#60a5fa', to: '#1d4ed8' },
    fsdPart: '5',
    inLauncher: true,
  },
  {
    id: 'crm',
    partId: 'p06-crm',
    labelKey: 'nav.apps.crm.label',
    descriptionKey: 'nav.apps.crm.description',
    glyph: 'contact',
    tint: { from: '#f472b6', to: '#be185d' },
    fsdPart: '6',
    inLauncher: true,
  },
  {
    id: 'inventory',
    partId: 'p07-inventory',
    labelKey: 'nav.apps.inventory.label',
    descriptionKey: 'nav.apps.inventory.description',
    glyph: 'package',
    tint: { from: '#fbbf24', to: '#b45309' },
    fsdPart: '7',
    inLauncher: true,
  },
  {
    id: 'manufacturing',
    partId: 'p08-manufacturing',
    labelKey: 'nav.apps.manufacturing.label',
    descriptionKey: 'nav.apps.manufacturing.description',
    glyph: 'factory',
    tint: { from: '#94a3b8', to: '#334155' },
    fsdPart: '8',
    inLauncher: true,
  },
  {
    id: 'hcm',
    partId: 'p09-hcm',
    labelKey: 'nav.apps.hcm.label',
    descriptionKey: 'nav.apps.hcm.description',
    glyph: 'users',
    tint: { from: '#a78bfa', to: '#6d28d9' },
    fsdPart: '9',
    inLauncher: true,
  },
  {
    id: 'pos',
    partId: 'p10-pos',
    labelKey: 'nav.apps.pos.label',
    descriptionKey: 'nav.apps.pos.description',
    glyph: 'store',
    tint: { from: '#f87171', to: '#b91c1c' },
    fsdPart: '10',
    inLauncher: true,
  },
  {
    id: 'pms',
    partId: 'p11-pms',
    labelKey: 'nav.apps.pms.label',
    descriptionKey: 'nav.apps.pms.description',
    glyph: 'bed',
    tint: { from: '#22d3ee', to: '#0e7490' },
    fsdPart: '11',
    inLauncher: true,
  },
  {
    id: 'projects',
    partId: 'p12-projects',
    labelKey: 'nav.apps.projects.label',
    descriptionKey: 'nav.apps.projects.description',
    glyph: 'kanban',
    tint: { from: '#facc15', to: '#a16207' },
    fsdPart: '12',
    inLauncher: true,
  },
  {
    id: 'service',
    partId: 'p13-service',
    labelKey: 'nav.apps.service.label',
    descriptionKey: 'nav.apps.service.description',
    glyph: 'headset',
    tint: { from: '#a3e635', to: '#4d7c0f' },
    fsdPart: '13',
    inLauncher: true,
  },
  {
    id: 'eam',
    partId: 'p14-eam',
    labelKey: 'nav.apps.eam.label',
    descriptionKey: 'nav.apps.eam.description',
    glyph: 'wrench',
    tint: { from: '#d6a77a', to: '#7c4a23' },
    fsdPart: '14',
    inLauncher: true,
  },
  {
    id: 'revenue',
    partId: 'p15-revenue',
    labelKey: 'nav.apps.revenue.label',
    descriptionKey: 'nav.apps.revenue.description',
    glyph: 'trendingUp',
    tint: { from: '#e879f9', to: '#a21caf' },
    fsdPart: '15',
    inLauncher: true,
  },
  {
    id: 'platform',
    partId: 'p16-platform',
    labelKey: 'nav.apps.platform.label',
    descriptionKey: 'nav.apps.platform.description',
    glyph: 'layers',
    tint: { from: '#71717a', to: '#27272a' },
    fsdPart: 'D · 17',
    inLauncher: true,
  },
  {
    id: 'me',
    partId: 'me',
    labelKey: 'nav.apps.me.label',
    descriptionKey: 'nav.apps.me.description',
    glyph: 'userCircle',
    tint: { from: '#38bdf8', to: '#0369a1' },
    fsdPart: null,
    inLauncher: true,
  },
]

const PART_BY_ID = new Map(NAV_PARTS.map((part) => [part.id, part]))

/** The full (unfiltered) part an app presents. */
export function appPart(app: NavApp): NavPart {
  const part = PART_BY_ID.get(app.partId)
  if (part === undefined) throw new Error(`nav/apps: no NavPart with id "${app.partId}"`)
  return part
}

export function appById(id: string): NavApp | undefined {
  return NAV_APPS.find((app) => app.id === id)
}

/** An app together with its part, already filtered to what the user may open. */
export interface VisibleApp {
  app: NavApp
  part: NavPart
}

/**
 * Apps the user can open, each with its part filtered by permission exactly
 * as `visibleParts` filters it — so an app with no reachable screen simply
 * has no tile, the same rule the sidebar has always applied to items
 * (CLAUDE.md §6: no greyed-out doors).
 */
export function visibleApps(can: PermissionCheck): VisibleApp[] {
  const parts = new Map(visibleParts(NAV_PARTS, can).map((part) => [part.id, part]))
  return NAV_APPS.flatMap((app) => {
    const part = parts.get(app.partId)
    return part === undefined ? [] : [{ app, part }]
  })
}

/**
 * Where opening an app takes you: its first reachable screen, in menu order.
 * Pass the PERMISSION-FILTERED part (from `visibleApps`), never `appPart`'s
 * raw one, or a user could be sent to a screen they would only be refused.
 * The launcher and the home grid both call this, so the same tile always
 * opens the same screen wherever it is clicked.
 */
export function appLandingPath(part: NavPart): string {
  return part.sections[0]?.items[0]?.to ?? '/'
}

/** Every item in an app, flattened across its sections. */
export function appItems(part: NavPart): NavItem[] {
  return part.sections.flatMap((section) => section.items)
}

function matches(item: NavItem, path: string): boolean {
  // Segment-aware, unlike `itemForPath`'s bare `startsWith`: `/me` must not
  // claim `/media/…` for the Me app.
  return item.to === '/' ? path === '/' : path === item.to || path.startsWith(`${item.to}/`)
}

/**
 * The app a route belongs to, by the same longest-prefix rule `itemForPath`
 * uses for breadcrumbs.
 *
 * No screen is listed in two apps today (Timesheets, `/projects/timesheets`,
 * is in Me only), but nothing in the nav data forbids it. If one ever is,
 * `preferAppId` — the app the user is already in — wins, so following a link
 * inside one app does not yank the sidebar over to the other. Returns
 * `undefined` for a path no nav item covers (an error page, a deep link with
 * no menu entry); the caller keeps whatever app it last had.
 *
 * `apps` is a test seam: the preference branch can only be exercised against
 * a synthetic app set, since the real one has no shared screen.
 */
export function appForPath(
  path: string,
  preferAppId?: string,
  apps: readonly { app: NavApp; part: NavPart }[] = NAV_APPS.map((app) => ({ app, part: appPart(app) })),
): NavApp | undefined {
  let best = -1
  let hits: NavApp[] = []
  for (const { app, part } of apps) {
    for (const item of appItems(part)) {
      if (!matches(item, path)) continue
      const length = item.to.length
      if (length > best) {
        best = length
        hits = [app]
      } else if (length === best && !hits.includes(app)) {
        hits.push(app)
      }
    }
  }
  if (hits.length === 0) return aliasApp(path, apps)
  return hits.find((app) => app.id === preferAppId) ?? hits[0]
}

/**
 * A screen reached only from inside another screen (`nav/aliases.ts`) belongs
 * to the app that holds the item it resolves to — so `/budget/versions/7`
 * opens Finance's sidebar, not whichever app the user was last in.
 */
function aliasApp(
  path: string,
  apps: readonly { app: NavApp; part: NavPart }[],
): NavApp | undefined {
  const alias = [...NAV_ROUTE_ALIASES]
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find((candidate) => matches({ to: candidate.prefix } as NavItem, path))
  if (alias === undefined) return undefined
  return apps.find(({ part }) => appItems(part).some((item) => item.id === alias.itemId))?.app
}

/** True when any item in the part carries the FSD extraneous marker. */
export function partHasExtraneous(part: NavPart): boolean {
  return part.sections.some((section) => section.items.some(isExtraneous))
}

/**
 * Whether one item is a recorded deviation from the FSD (C17: users, roles,
 * profile). Item-level only — sections no longer carry the marker.
 */
export function isExtraneous(item: NavItem): boolean {
  return item.fsdStatus === 'extraneous'
}

/**
 * Item id → the section that lists it, built once at module load. If an id
 * ever appears in two sections, the first in `NAV_PARTS` order wins.
 */
const SECTION_BY_ITEM_ID: ReadonlyMap<string, NavGroup> = (() => {
  const map = new Map<string, NavGroup>()
  for (const part of NAV_PARTS) {
    for (const section of part.sections) {
      for (const item of section.items) {
        if (!map.has(item.id)) map.set(item.id, section)
      }
    }
  }
  return map
})()

/** The section (in the unfiltered nav) that lists this item, by item id. */
export function sectionOf(item: NavItem): NavGroup | undefined {
  return SECTION_BY_ITEM_ID.get(item.id)
}

/**
 * An item's effective FSD reference, inheriting from its section.
 *
 * `fsd` falls back to the section's number. `code` falls back to the
 * section's code only when the item is still within the section's spec — it
 * has no number of its own, the same number, or a sub-section of it
 * (`'1.2.7.3'` under `'1.2.7'`). An item that declares a different number
 * (`'10.2.8'` filed under an Inventory section) is a different spec, so the
 * section's feature code would be wrong for it and is not inherited.
 */
export function resolveFsd(item: NavItem, section: NavGroup | undefined): { fsd?: string; code?: string } {
  const fsd = item.fsd ?? section?.fsd
  const sectionFsd = section?.fsd
  const inherits =
    sectionFsd !== undefined &&
    (item.fsd === undefined || item.fsd === sectionFsd || item.fsd.startsWith(`${sectionFsd}.`))
  const code = item.code ?? (inherits ? section?.code : undefined)
  return { fsd, code }
}

export interface NavFsdRef {
  fsd?: string
  code?: string
  /** The section the reference was resolved against, if the item is in the nav. */
  section?: NavGroup
}

/** `resolveFsd` against the item's own section in the real nav. */
export function navFsdOf(item: NavItem): NavFsdRef {
  const section = sectionOf(item)
  return { ...resolveFsd(item, section), section }
}
